import React, { useContext, useEffect } from 'react';
import moment from 'moment/moment';
import { Match } from 'effect';
import _, { entries, fromPairs, groupBy, max, min, uniq } from 'lodash';
import { assertNotNil, getDaysInMonthCalendar } from 'venn-utils';
import { useThirtyDayCalendarActions } from './useThirtyDayCalendarActions';
import { AllocationAsOfDateDropdownContext } from '../AllocationAsOfDateDropdownContext';

type UseThirtyDayCalendarProps = {
  asOfDateTimestamp: number;
  allocationDates: number[];
};
export const useThirtyDayCalendar = ({ asOfDateTimestamp, allocationDates }: UseThirtyDayCalendarProps) => {
  const {
    mutations: { setAllocationsAsOfDate, closeDropdownPanel },
  } = useContext(AllocationAsOfDateDropdownContext);
  const [selectedYear, setSelectedYear] = React.useState<number>(moment.utc(asOfDateTimestamp).year());
  const [selectedMonth, setSelectedMonth] = React.useState<number>(moment.utc(asOfDateTimestamp).month());
  useEffect(() => {
    setSelectedYear(moment.utc(asOfDateTimestamp).year());
    setSelectedMonth(moment.utc(asOfDateTimestamp).month());
  }, [asOfDateTimestamp]);
  const yearToMonthsWithAllocations = fromPairs(
    entries(
      groupBy(
        allocationDates.map((d) => moment.utc(d)),
        (d) => d.year(),
      ),
    ).map(([year, dates]) => [year, uniq(dates.map((d) => d.month()))]),
  );
  const allYears = Object.keys(yearToMonthsWithAllocations)
    .map(Number)
    .sort((a, b) => b - a);
  const earliestYear = assertNotNil(min(allYears));
  const finalYear = assertNotNil(max(allYears));
  const monthsToDisplay = Match.value(selectedYear).pipe(
    Match.when(
      (year) => year === finalYear,
      () => {
        const today = moment.utc();
        return _.range(0, today.month() + 1);
      },
    ),
    Match.when(
      (year) => year === earliestYear,
      () => _.range(0, 12),
    ),
    Match.orElse(() => _.range(0, 12)),
  );
  const monthsWithAllocation = yearToMonthsWithAllocations[selectedYear] ?? [];
  const cells = getDaysInMonthCalendar(moment.utc().year(selectedYear).month(selectedMonth));
  const performAction = useThirtyDayCalendarActions({
    state: {
      yearToMonthsWithAllocations,
      selectedYear,
      selectedMonth,
    },
    mutations: {
      setSelectedYear,
      setSelectedMonth,
      setAllocationsAsOfDate,
      closeDropdownPanel,
    },
  });
  return {
    performAction,
    state: {
      selectedYear,
      selectedMonth,
      allYears,
      monthsToDisplay,
      monthsWithAllocation,
      cells,
    },
  };
};
